import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Survey.css';

interface SurveyQuestion {
  id: string;
  category: 'schedule' | 'mind' | 'diet' | 'exercise' | 'general' | 'local';
  question: string;
  subtext?: string;
  type: 'text' | 'select' | 'multiselect' | 'number' | 'zipcode' | 'daily_schedule' | 'cooking_days' | 'sleep_schedule';
  options?: string[];
  optional?: boolean;
}

interface WorkoutDay {
  day: string;
  name: string;
  modality: string;
  notes: string;
  strength_focus: string;
  equipment: string;
  time: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  duration_minutes: number;
  other_notes: string;
}

interface SurveyAnswers {
  workout_types: string[];
  best_days: string[];
  preferred_time: string;
  sessions_per_week: string;
  duration: string;
  intensity: string;
  other_notes: string;
}

type BuilderMode = 'build' | 'survey';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const MODALITY_OPTIONS = [
  { label: 'Running',      value: 'running',     defaultName: 'Run',               hasNotes: false },
  { label: 'Strength',     value: 'strength',    defaultName: 'Strength Training',  hasNotes: false },
  { label: 'HIIT',         value: 'hiit',        defaultName: 'HIIT Workout',       hasNotes: false },
  { label: 'Yoga',         value: 'yoga',        defaultName: 'Yoga Session',       hasNotes: false },
  { label: 'Mobility',     value: 'mobility',    defaultName: 'Mobility Work',      hasNotes: false },
  { label: 'Core',         value: 'core',        defaultName: 'Core Session',       hasNotes: false },
  { label: 'Stretch',      value: 'stretch',     defaultName: 'Stretch',            hasNotes: false },
  { label: 'Group Class',  value: 'group_class', defaultName: 'Group Class',        hasNotes: true  },
  { label: 'Sport',        value: 'sport',       defaultName: 'Sport',              hasNotes: true  },
  { label: 'Other Cardio', value: 'cardio',      defaultName: 'Cardio Session',     hasNotes: true  },
  { label: 'Other',        value: 'other',       defaultName: 'Workout',            hasNotes: true  },
];

const DEFAULT_NAMES    = Object.fromEntries(MODALITY_OPTIONS.map(o => [o.value, o.defaultName]));
const ALL_DEFAULT_NAMES = new Set(MODALITY_OPTIONS.map(o => o.defaultName));
const NOTES_MODALITIES  = new Set(MODALITY_OPTIONS.filter(o => o.hasNotes).map(o => o.value));

const DIFFICULTY_OPTIONS: { label: string; value: 'beginner' | 'intermediate' | 'advanced' }[] = [
  { label: 'Beginner',     value: 'beginner'     },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced',     value: 'advanced'     },
];

const DURATION_OPTIONS = [15, 20, 30, 45, 60, 90];

// 30-min slots 5 AM – 10 PM (avoids native time-picker mobile issues)
const TIME_OPTIONS: { label: string; value: string }[] = (() => {
  const opts: { label: string; value: string }[] = [{ label: 'Any time', value: '' }];
  for (let h = 5; h <= 22; h++) {
    for (const m of [0, 30]) {
      if (h === 22 && m === 30) continue;
      const hh = String(h).padStart(2, '0');
      const mm = m === 0 ? '00' : '30';
      const ampm = h < 12 ? 'AM' : 'PM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      opts.push({ label: `${h12}:${mm} ${ampm}`, value: `${hh}:${mm}` });
    }
  }
  return opts;
})();

const STRENGTH_FOCUS = [
  { label: 'Upper Body', value: 'upper_body' },
  { label: 'Lower Body', value: 'lower_body' },
  { label: 'Core',       value: 'core'       },
  { label: 'Full Body',  value: 'full_body'  },
];

const EQUIPMENT = [
  { label: 'Bodyweight',   value: 'bodyweight'   },
  { label: 'Free Weights', value: 'free_weights' },
  { label: 'Full Gym',     value: 'full_gym'     },
];

const NOTES_LABEL: Record<string, string> = {
  group_class: 'Class name', sport: 'Sport / activity', cardio: 'Activity', other: 'Activity name',
};
const NOTES_PLACEHOLDER: Record<string, string> = {
  group_class: 'e.g. Pilates, Spin, Barre...',
  sport:       'e.g. Tennis, Basketball, Soccer...',
  cardio:      'e.g. Cycling, Rowing, Elliptical...',
  other:       'e.g. Rock climbing, Dance, Martial arts...',
};

const SURVEY_WORKOUT_TYPES = [
  { label: 'Running',        value: 'running'     },
  { label: 'Strength',       value: 'strength'    },
  { label: 'HIIT',           value: 'hiit'        },
  { label: 'Yoga',           value: 'yoga'        },
  { label: 'Mobility',       value: 'mobility'    },
  { label: 'Core & Stretch', value: 'core'        },
  { label: 'Group Classes',  value: 'group_class' },
  { label: 'Sports',         value: 'sport'       },
  { label: 'Cycling/Cardio', value: 'cardio'      },
];

const SURVEY_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SURVEY_TIMES = [
  { label: 'Early morning', value: 'early_morning', desc: '5–8 AM'    },
  { label: 'Morning',       value: 'morning',       desc: '8–10 AM'   },
  { label: 'Midday',        value: 'midday',        desc: '10 AM–1 PM'},
  { label: 'Afternoon',     value: 'afternoon',     desc: '1–5 PM'    },
  { label: 'Evening',       value: 'evening',       desc: '5–9 PM'    },
  { label: 'Flexible',      value: 'flexible',      desc: 'Any time'  },
];

const SURVEY_SPW = [
  { label: '2–3 days', value: '2-3' },
  { label: '3–4 days', value: '3-4' },
  { label: '4–5 days', value: '4-5' },
  { label: '5+ days',  value: '5+'  },
];

const SURVEY_DURATION = [
  { label: '< 30 min',  value: 'under_30' },
  { label: '30–45 min', value: '30_45'    },
  { label: '45–60 min', value: '45_60'    },
  { label: '60+ min',   value: 'over_60'  },
];

const SURVEY_INTENSITY = [
  { label: 'Light',       value: 'light',       desc: 'Active recovery, low sweat'    },
  { label: 'Moderate',    value: 'moderate',    desc: 'Challenging but sustainable'   },
  { label: 'Challenging', value: 'challenging', desc: 'Push yourself most sessions'   },
  { label: 'Max effort',  value: 'max',         desc: 'Go hard every time'            },
];


const blankSession = (difficulty: WorkoutDay['difficulty'] = 'beginner'): WorkoutDay => ({
  day: '', name: '', modality: '', notes: '', strength_focus: '',
  equipment: '', time: '', difficulty, duration_minutes: 45, other_notes: '',
});

const blankSurveyAnswers = (): SurveyAnswers => ({
  workout_types: [], best_days: [], preferred_time: '',
  sessions_per_week: '', duration: '', intensity: '', other_notes: '',
});

const BEDTIME_OPTIONS = [
  { label: '8:00 PM',  value: '20:00' }, { label: '8:30 PM',  value: '20:30' },
  { label: '9:00 PM',  value: '21:00' }, { label: '9:30 PM',  value: '21:30' },
  { label: '10:00 PM', value: '22:00' }, { label: '10:30 PM', value: '22:30' },
  { label: '11:00 PM', value: '23:00' }, { label: '11:30 PM', value: '23:30' },
  { label: 'Midnight', value: '00:00' }, { label: '12:30 AM', value: '00:30' },
  { label: '1:00 AM',  value: '01:00' }, { label: '1:30 AM',  value: '01:30' },
  { label: '2:00 AM+', value: '02:00' },
];

const WAKETIME_OPTIONS = [
  { label: '4:00 AM',  value: '04:00' }, { label: '4:30 AM',  value: '04:30' },
  { label: '5:00 AM',  value: '05:00' }, { label: '5:30 AM',  value: '05:30' },
  { label: '6:00 AM',  value: '06:00' }, { label: '6:30 AM',  value: '06:30' },
  { label: '7:00 AM',  value: '07:00' }, { label: '7:30 AM',  value: '07:30' },
  { label: '8:00 AM',  value: '08:00' }, { label: '8:30 AM',  value: '08:30' },
  { label: '9:00 AM',  value: '09:00' }, { label: '9:30 AM',  value: '09:30' },
  { label: '10:00 AM', value: '10:00' }, { label: '11:00 AM', value: '11:00' },
];

const SCHEDULE_SLOTS = [
  {
    key: 'wake_up',
    label: 'Wake up',
    options: ['4am', '5am', '5:30am', '6am', '6:30am', '7am', '7:30am', '8am', '8:30am', '9am', '10am', '11am or later'],
  },
  {
    key: 'breakfast',
    label: 'Breakfast',
    options: ['5am', '6am', '7am', '7:30am', '8am', '8:30am', '9am', '9:30am', '10am', 'Later', 'I skip breakfast'],
  },
  {
    key: 'lunch',
    label: 'Lunch',
    options: ['10am', '11am', '11:30am', '12pm', '12:30pm', '1pm', '1:30pm', '2pm', 'Later', 'I skip lunch'],
  },
  {
    key: 'dinner',
    label: 'Dinner',
    options: ['4pm', '5pm', '5:30pm', '6pm', '6:30pm', '7pm', '7:30pm', '8pm', '9pm or later'],
  },
  {
    key: 'exercise',
    label: 'Exercise',
    options: ['Before 7am', '7–10am', '10am–1pm', '1–5pm', '5–8pm', 'After 8pm', 'Varies', "I don't exercise regularly"],
  },
  {
    key: 'wind_down',
    label: 'Wind down / sleep',
    options: ['8pm', '8:30pm', '9pm', '9:30pm', '10pm', '10:30pm', '11pm', 'Midnight or later'],
  },
];

const surveyQuestions: SurveyQuestion[] = [
  // -----------------------------
  // DIET
  // -----------------------------
  {
    id: 'daily_schedule',
    category: 'schedule',
    question: 'What does your typical day look like?',
    subtext: "We'll use this to time check-ins around your actual schedule and know what's realistic for you.",
    type: 'daily_schedule',
    optional: true,
  },
  {
    id: 'diet_grocery_day',
    category: 'diet',
    question: 'When do you usually do your grocery shopping (including online ordering)?',
    type: 'multiselect',
    options: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Varies week-to-week',
    ],
  },
  {
    id: 'diet_cuisines_enjoy',
    category: 'diet',
    question: 'What types of cuisine do you enjoy or want to try more of? (Select all that apply)',
    type: 'multiselect',
    options: [
      'American',
      'Mediterranean',
      'Italian',
      'Mexican',
      'Asian (Chinese, Japanese, Thai, etc.)',
      'Indian',
      'Middle Eastern',
      'French',
      'Spanish',
      'Plant-based / vegan',
      'Seafood-focused',
      'No preference / open to anything',
    ],
  },
  {
    id: 'diet_dislikes',
    category: 'diet',
    question: 'Are there any foods you strongly dislike or always want to avoid? (Select all that apply)',
    subtext: 'This is separate from allergies — just foods you\'d rather not see suggested.',
    type: 'multiselect',
    options: [
      'Mushrooms',
      'Shellfish',
      'Spicy food',
      'Lamb or game meats',
      'Liver / organ meats',
      'Strong cheeses (blue, brie, etc.)',
      'Raw fish / sushi',
      'Tofu / tempeh',
      'Olives',
      'Cilantro',
      'Beets',
      'Brussels sprouts',
      'No strong dislikes',
    ],
    optional: true,
  },
  {
    id: 'diet_restrictions',
    category: 'diet',
    question: 'Do you have any dietary restrictions, allergies, or preferences? (e.g., vegetarian, gluten-free, lactose intolerant)',
    type: 'text',
    optional: true,
  },
  {
    id: 'diet_cooking_days',
    category: 'diet',
    question: 'Which days are usually best for you to cook at home?',
    type: 'cooking_days',
  },
  {
    id: 'general_household',
    category: 'diet',
    question: 'Who do you usually cook for or eat with?',
    type: 'select',
    options: [
      'Just me',
      'Me and a partner',
      'Family with kids',
      'Roommates / shared household',
      'Varies a lot',
    ],
    optional: true,
  },

  // -----------------------------
  // MIND
  // -----------------------------
  {
    id: 'mind_stress_timing',
    category: 'mind',
    question: 'When do you typically feel stress?',
    type: 'select',
    options: [
      'During weekdays (work, school, etc.)',
      'On weekends',
      'Both equally',
      'Other / no clear pattern',
    ],
  },
  {
    id: 'mind_stress_relief',
    category: 'mind',
    question: 'Which stress relief strategies do you already practice or are interested in? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Exercise or physical activity',
      'Meditation or mindfulness',
      'Deep breathing exercises',
      'Journaling or writing',
      'Spending time outdoors',
      'Listening to music or podcasts',
      'Talking to friends or family',
      'Therapy/counseling',
      'Reading',
      'Yoga or stretching',
      'Creative hobbies (art, music, crafts)',
    ],
  },
  {
    id: 'mind_activities',
    category: 'mind',
    question: 'What activities are part of your typical week? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Work',
      'Attend School',
      'Parenting',
      'Caretaking',
      'Household chores',
      'Reading books',
      'Writing/journaling',
      'Puzzles (crossword, sudoku, etc.)',
      'Creative design',
      'Strategy games',
      'Other',
    ],
  },

  // -----------------------------
  // EXERCISE
  // -----------------------------
  {
    id: 'move_types',
    category: 'exercise',
    question: 'Which types of exercise or movement do you enjoy or prefer? (Select all that apply)',
    type: 'multiselect',
    options: [
      'Walking',
      'Running',
      'Cycling',
      'Strength training',
      'Yoga or Pilates',
      'Sports or recreational activities',
      'Hiking/outdoors',
      'Swimming/water workouts',
      'At-home workouts',
      'No preference / unsure',
    ],
  },
  {
    id: 'move_physical_limitations',
    category: 'exercise',
    question: 'Do you have any physical limitations we should know about?',
    type: 'text',
    optional: true,
  },

  // -----------------------------
  // LOCAL SUGGESTIONS
  // -----------------------------
  {
    id: 'zip_code',
    category: 'local',
    question: "What's your zip code?",
    subtext: "We'll use this to include local weather and nearby event suggestions in your daily check-ins. Totally optional — skip if you'd prefer not to share.",
    type: 'zipcode',
    optional: true,
  },
  {
    id: 'local_event_types',
    category: 'local',
    question: 'What kinds of local activities interest you?',
    subtext: "On Thursdays we'll surface local events near you that match your interests. Select as many as you like, or skip.",
    type: 'multiselect',
    options: [
      'Live Music & Concerts',
      'Food & Drink Events (festivals, tastings, markets)',
      'Family & Kid-Friendly',
      'Outdoors & Nature (hikes, walks, parks, markets)',
      'Fitness & Wellness (yoga, group runs, classes)',
      'Arts & Culture (museums, galleries, theater, comedy)',
      'Festivals & Seasonal Events',
      'Workshops & Classes (cooking, crafts, learning)',
      'Sports (watching or participating)',
      'Community & Local Gatherings (farmers markets, library events, meetups)',
    ],
    optional: true,
  },

  // -----------------------------
  // GENERAL
  // -----------------------------
  {
    id: 'general_additional',
    category: 'general',
    question: 'Anything else we should know about your diet, exercise, mental wellness, or lifestyle?',
    type: 'text',
    optional: true,
  },
];

function Survey() {
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, string | string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showWorkoutBuilder, setShowWorkoutBuilder] = useState(false);
  const [workoutDays, setWorkoutDays] = useState<WorkoutDay[]>([]);
  const [builderMode, setBuilderMode] = useState<BuilderMode>('build');
  const [surveyAnswers, setSurveyAnswers] = useState<SurveyAnswers>(blankSurveyAnswers());
  const { completeSurvey } = useAuth();
  const navigate = useNavigate();

  // After Stripe checkout redirect, verify session to ensure subscription is active
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      (async () => {
        try {
          const { auth: fbAuth } = await import('../config/firebase');
          const idToken = await fbAuth?.currentUser?.getIdToken();
          if (!idToken) return;
          const API_BASE = (import.meta as any).env?.VITE_API_BASE || '';
          await fetch(`${API_BASE}/subscription/verify-session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({ sessionId }),
          });
        } catch { /* best-effort; webhook may handle it */ }
      })();
      window.history.replaceState({}, '', '/survey');
    }
  }, []);

  const totalSteps = surveyQuestions.length + 1;
  const currentQuestion = surveyQuestions[currentStep];
  const progress = showWorkoutBuilder ? 100 : ((currentStep + 1) / totalSteps) * 100;

  const handleTextChange = (value: string) => {
    setResponses(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleNumberChange = (value: string) => {
    setResponses(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleSelectChange = (value: string) => {
    setResponses(prev => ({ ...prev, [currentQuestion.id]: value }));
  };

  const handleMultiSelectChange = (value: string) => {
    setResponses(prev => {
      const currentValues = (prev[currentQuestion.id] as string[]) || [];
      if (currentValues.includes(value)) {
        return { ...prev, [currentQuestion.id]: currentValues.filter(v => v !== value) };
      }
      return { ...prev, [currentQuestion.id]: [...currentValues, value] };
    });
  };

  const handleScheduleChange = (field: string, value: string) => {
    setResponses(prev => ({ ...prev, [`schedule_${field}`]: value }));
  };

  const parseCookingDays =(val: string | string[] | undefined): { flexible: boolean; days: { day: string; time: string }[] } => {
    if (!val) return { flexible: false, days: [] };
    try {
      const parsed = JSON.parse(val as string);
      if (parsed.flexible) return { flexible: true, days: [] };
      return { flexible: false, days: parsed.days ?? [] };
    } catch { return { flexible: false, days: [] }; }
  };

  const handleCookingDaysFlexible = (questionId: string, currentlyFlexible: boolean) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: currentlyFlexible
        ? JSON.stringify({ days: [] })
        : JSON.stringify({ flexible: true }),
    }));
  };

  const handleCookingDaysToggleDay = (questionId: string, day: string) => {
    setResponses(prev => {
      const { days } = parseCookingDays(prev[questionId]);
      const exists = days.some(d => d.day === day);
      const newDays = exists ? days.filter(d => d.day !== day) : [...days, { day, time: '' }];
      return { ...prev, [questionId]: JSON.stringify({ days: newDays }) };
    });
  };

  const handleSleepScheduleChange = (field: 'bedtime' | 'wake_time', value: string) => {
    setResponses(prev => {
      const existing = prev[currentQuestion.id];
      const data: Record<string, string> = existing ? JSON.parse(existing as string) : {};
      data[field] = value;
      return { ...prev, [currentQuestion.id]: JSON.stringify(data) };
    });
  };

  const handleCookingDaysTime = (questionId: string, day: string, time: string) => {
    setResponses(prev => {
      const { days } = parseCookingDays(prev[questionId]);
      return { ...prev, [questionId]: JSON.stringify({ days: days.map(d => d.day === day ? { ...d, time } : d) }) };
    });
  };

  const addSession = () => {
    setWorkoutDays(prev => [...prev, blankSession('beginner')]);
  };

  const removeSession = (index: number) => {
    setWorkoutDays(prev => prev.filter((_, i) => i !== index));
  };

  const duplicateSession = (i: number) =>
    setWorkoutDays(prev => [...prev.slice(0, i + 1), { ...prev[i], day: '' }, ...prev.slice(i + 1)]);

  const updateDay = (index: number, field: keyof WorkoutDay, value: string | number) => {
    setWorkoutDays(prev =>
      prev.map((d, i) => i === index ? { ...d, [field]: value } : d)
    );
  };

  const updateModality = (index: number, newModality: string) => {
    setWorkoutDays(prev =>
      prev.map((d, i) => {
        if (i !== index) return d;
        const nameIsDefault = ALL_DEFAULT_NAMES.has(d.name) || d.name === '';
        return {
          ...d,
          modality:       newModality,
          name:           nameIsDefault ? (DEFAULT_NAMES[newModality] ?? 'Workout') : d.name,
          notes:          '',
          strength_focus: newModality === 'strength' ? d.strength_focus : '',
          equipment:      newModality === 'strength' ? d.equipment : '',
        };
      })
    );
  };

  const toggleStrengthPill = (i: number, field: 'strength_focus' | 'equipment', val: string) =>
    setWorkoutDays(prev => prev.map((d, idx) =>
      idx === i ? { ...d, [field]: d[field] === val ? '' : val } : d
    ));

  const toggleSurveyMulti = (field: 'workout_types' | 'best_days', val: string) =>
    setSurveyAnswers(prev => {
      const cur = prev[field];
      return { ...prev, [field]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] };
    });

  const setSurveyField = (field: keyof SurveyAnswers, val: string) =>
    setSurveyAnswers(prev => ({ ...prev, [field]: val }));

  const surveyBuilderValid = surveyAnswers.workout_types.length > 0 && surveyAnswers.best_days.length > 0;

  const canProceed = () => {
    if (currentQuestion.type === 'daily_schedule') return true;
    if (currentQuestion.type === 'zipcode') {
      const val = (responses[currentQuestion.id] as string) || '';
      return val.length === 0 || val.length === 5;
    }
    if (currentQuestion.type === 'cooking_days') {
      const { flexible, days } = parseCookingDays(responses[currentQuestion.id]);
      return flexible || days.length > 0;
    }
    if (currentQuestion.type === 'sleep_schedule') {
      const val = responses[currentQuestion.id];
      if (!val) return false;
      const data = JSON.parse(val as string);
      return !!(data.bedtime && data.wake_time);
    }
    if (currentQuestion.optional) return true;
    const response = responses[currentQuestion.id];
    if (!response) return false;
    if (Array.isArray(response)) return response.length > 0;
    return response.trim().length > 0;
  };

  const handleNext = () => {
    if (currentStep < surveyQuestions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowWorkoutBuilder(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (routineJson?: string, surveyJson?: string) => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const formattedResponses: Record<string, string> = {};
      Object.entries(responses).forEach(([key, value]) => {
        if (key === 'zip_code' && (!value || (value as string).trim() === '')) return;
        formattedResponses[key] = Array.isArray(value) ? JSON.stringify(value) : value;
      });
      if (!formattedResponses['local_event_types']) {
        formattedResponses['local_event_types'] = '[]';
      }
      if (routineJson) {
        formattedResponses['workout_routine'] = routineJson;
      }
      if (surveyJson) {
        formattedResponses['workout_survey_answers'] = surveyJson;
      }

      await completeSurvey(formattedResponses);
      navigate('/focus-areas');
    } catch (error) {
      console.error('Failed to submit survey:', error);
      setSubmitError('We couldn’t save your survey. Your answers are still here — please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'schedule': return 'Your Schedule';
      case 'mind': return 'Mind & Mental Wellness';
      case 'diet': return 'Diet & Nutrition';
      case 'exercise': return 'Exercise & Movement';
      case 'local': return 'Local Suggestions (Optional)';
      default: return 'General';
    }
  };

  return (
    <div className="survey-page">
      <div className="survey-container">
        <div className="survey-header">
          <h1>Let's Get to Know You</h1>
          <p>Your answers will help personalize your wellness journey</p>
        </div>

        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="progress-text">
          {showWorkoutBuilder
            ? 'Final step — almost there!'
            : `Question ${currentStep + 1} of ${totalSteps}`}
        </div>

        {showWorkoutBuilder ? (
          <div className="question-card workout-builder">
            <div className="question-category">
              <span className="category-label">Exercise & Movement</span>
            </div>
            <h2 className="question-text">Build Your Weekly Routine</h2>
            <p className="question-subtext">
              We'll generate a personalized 4-week plan. Choose how you'd like to set it up.
            </p>

            {/* ── Mode switcher ── */}
            <div className="session-mode-row">
              <button
                type="button"
                className={`session-mode-btn${builderMode === 'build' ? ' session-mode-btn--active' : ''}`}
                onClick={() => setBuilderMode('build')}
              >
                <span className="session-mode-label">Build it myself</span>
                <span className="session-mode-desc">Choose your days, times, and workout types</span>
              </button>
              <button
                type="button"
                className={`session-mode-btn${builderMode === 'survey' ? ' session-mode-btn--active' : ''}`}
                onClick={() => setBuilderMode('survey')}
              >
                <span className="session-mode-label">Quick survey</span>
                <span className="session-mode-desc">Answer a few questions, AI builds your plan</span>
              </button>
            </div>

            {/* ── Build mode ── */}
            {builderMode === 'build' && (
              <>
                <div className="session-list">
                  {workoutDays.map((session, index) => (
                    <div className="session-card" key={index}>

                      {/* Header: day + remove */}
                      <div className="session-card-header">
                        <select
                          className="session-select session-day"
                          value={session.day}
                          onChange={e => updateDay(index, 'day', e.target.value)}
                        >
                          <option value="">Select day...</option>
                          {DAYS_OF_WEEK.map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <button
                          className="session-remove-btn"
                          onClick={() => removeSession(index)}
                          aria-label="Remove session"
                        >
                          ×
                        </button>
                      </div>

                      {/* Body */}
                      <div className="session-card-body">

                        {/* Session name */}
                        <div className="session-field session-field--full">
                          <span className="session-field-label">Session name</span>
                          <input
                            type="text"
                            className="session-name-input"
                            value={session.name}
                            onChange={e => updateDay(index, 'name', e.target.value)}
                            placeholder="e.g. Morning Run"
                            maxLength={80}
                          />
                        </div>

                        {/* Time */}
                        <div className="session-field session-field--full">
                          <span className="session-field-label">Time</span>
                          <select
                            className="session-select"
                            value={session.time}
                            onChange={e => updateDay(index, 'time', e.target.value)}
                          >
                            {TIME_OPTIONS.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Type pills */}
                        <div className="session-field session-field--full">
                          <span className="session-field-label">Type</span>
                          <div className="session-modality-pills">
                            {MODALITY_OPTIONS.map(opt => (
                              <button
                                key={opt.value}
                                type="button"
                                className={`session-pill${session.modality === opt.value ? ' session-pill--active' : ''}`}
                                onClick={() => updateModality(index, opt.value)}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Notes (group class, sport, other cardio, other) */}
                        {NOTES_MODALITIES.has(session.modality) && (
                          <div className="session-field session-field--full">
                            <span className="session-field-label">
                              {NOTES_LABEL[session.modality] ?? 'Activity'}
                            </span>
                            <input
                              type="text"
                              className="session-notes-input"
                              value={session.notes}
                              onChange={e => updateDay(index, 'notes', e.target.value)}
                              placeholder={NOTES_PLACEHOLDER[session.modality] ?? ''}
                              maxLength={80}
                            />
                          </div>
                        )}

                        {/* Strength sub-pickers */}
                        {session.modality === 'strength' && (
                          <div className="session-sub-section">
                            <div className="session-field session-field--full">
                              <span className="session-field-label">Focus area</span>
                              <div className="session-modality-pills">
                                {STRENGTH_FOCUS.map(opt => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    className={`session-pill${session.strength_focus === opt.value ? ' session-pill--active' : ''}`}
                                    onClick={() => toggleStrengthPill(index, 'strength_focus', opt.value)}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="session-field session-field--full">
                              <span className="session-field-label">Equipment</span>
                              <div className="session-modality-pills">
                                {EQUIPMENT.map(opt => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    className={`session-pill${session.equipment === opt.value ? ' session-pill--active' : ''}`}
                                    onClick={() => toggleStrengthPill(index, 'equipment', opt.value)}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Duration + Level side-by-side */}
                        <div className="session-card-fields">
                          <div className="session-field">
                            <span className="session-field-label">Duration</span>
                            <select
                              className="session-select"
                              value={session.duration_minutes}
                              onChange={e => updateDay(index, 'duration_minutes', Number(e.target.value))}
                            >
                              {DURATION_OPTIONS.map(min => (
                                <option key={min} value={min}>{min} min</option>
                              ))}
                            </select>
                          </div>
                          <div className="session-field">
                            <span className="session-field-label">Level</span>
                            <select
                              className="session-select"
                              value={session.difficulty}
                              onChange={e => updateDay(index, 'difficulty', e.target.value as WorkoutDay['difficulty'])}
                            >
                              {DIFFICULTY_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Other notes */}
                        <div className="session-field session-field--full">
                          <span className="session-field-label">Anything else?</span>
                          <textarea
                            className="session-other-notes"
                            rows={2}
                            value={session.other_notes}
                            onChange={e => updateDay(index, 'other_notes', e.target.value)}
                            placeholder="Injuries, equipment limits, specific goals..."
                            maxLength={300}
                          />
                        </div>

                        <button
                          type="button"
                          className="session-duplicate-btn"
                          onClick={() => duplicateSession(index)}
                        >
                          + Duplicate to another day
                        </button>

                      </div>
                    </div>
                  ))}
                </div>

                <button className="add-session-btn" onClick={addSession}>
                  + Add Workout Session
                </button>

                <p className="workout-skip-hint">
                  Not ready to plan?{' '}
                  <button
                    className="skip-link-btn"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                  >
                    Skip for now
                  </button>
                  {' '}— you can always text Mosaime to build a routine later.
                </p>
              </>
            )}

            {/* ── Survey mode ── */}
            {builderMode === 'survey' && (
              <div className="session-survey-card">

                <div className="session-survey-section">
                  <p className="session-survey-q">What types of workouts do you enjoy?</p>
                  <p className="session-survey-hint">Pick as many as you like</p>
                  <div className="session-modality-pills">
                    {SURVEY_WORKOUT_TYPES.map(opt => (
                      <button key={opt.value} type="button"
                        className={`session-pill${surveyAnswers.workout_types.includes(opt.value) ? ' session-pill--active' : ''}`}
                        onClick={() => toggleSurveyMulti('workout_types', opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="session-divider" />

                <div className="session-survey-section">
                  <p className="session-survey-q">Which days usually work best?</p>
                  <p className="session-survey-hint">Select all that apply</p>
                  <div className="session-modality-pills">
                    {SURVEY_DAYS.map(d => (
                      <button key={d} type="button"
                        className={`session-pill${surveyAnswers.best_days.includes(d) ? ' session-pill--active' : ''}`}
                        onClick={() => toggleSurveyMulti('best_days', d)}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="session-survey-section">
                  <p className="session-survey-q">What time of day works best?</p>
                  <div className="session-modality-pills">
                    {SURVEY_TIMES.map(opt => (
                      <button key={opt.value} type="button"
                        className={`session-pill${surveyAnswers.preferred_time === opt.value ? ' session-pill--active' : ''}`}
                        onClick={() => setSurveyField('preferred_time', surveyAnswers.preferred_time === opt.value ? '' : opt.value)}>
                        {opt.label} <span style={{ opacity: 0.6, fontSize: '11px' }}>· {opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="session-divider" />

                <div className="session-survey-section">
                  <p className="session-survey-q">How many days per week?</p>
                  <div className="session-modality-pills">
                    {SURVEY_SPW.map(opt => (
                      <button key={opt.value} type="button"
                        className={`session-pill${surveyAnswers.sessions_per_week === opt.value ? ' session-pill--active' : ''}`}
                        onClick={() => setSurveyField('sessions_per_week', surveyAnswers.sessions_per_week === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="session-survey-section">
                  <p className="session-survey-q">How long should each session be?</p>
                  <div className="session-modality-pills">
                    {SURVEY_DURATION.map(opt => (
                      <button key={opt.value} type="button"
                        className={`session-pill${surveyAnswers.duration === opt.value ? ' session-pill--active' : ''}`}
                        onClick={() => setSurveyField('duration', surveyAnswers.duration === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="session-survey-section">
                  <p className="session-survey-q">How intense should workouts be?</p>
                  <div className="session-modality-pills">
                    {SURVEY_INTENSITY.map(opt => (
                      <button key={opt.value} type="button"
                        className={`session-pill${surveyAnswers.intensity === opt.value ? ' session-pill--active' : ''}`}
                        onClick={() => setSurveyField('intensity', surveyAnswers.intensity === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="session-divider" />

                <div className="session-survey-section">
                  <p className="session-survey-q">Anything else we should know?</p>
                  <p className="session-survey-hint">Injuries, equipment access, specific goals...</p>
                  <textarea
                    className="session-other-notes"
                    rows={3}
                    value={surveyAnswers.other_notes}
                    onChange={e => setSurveyField('other_notes', e.target.value)}
                    placeholder="e.g. I have a bad knee. I only have dumbbells at home."
                    maxLength={400}
                  />
                </div>

                <p className="workout-skip-hint">
                  Not ready to plan?{' '}
                  <button
                    className="skip-link-btn"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                  >
                    Skip for now
                  </button>
                  {' '}— you can always text Mosaime to build a routine later.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="question-card">
            <div className="question-category">
              <span className="category-label">{getCategoryLabel(currentQuestion.category)}</span>
            </div>

            <h2 className="question-text">{currentQuestion.question}</h2>

            {currentQuestion.subtext && (
              <p className="question-subtext">{currentQuestion.subtext}</p>
            )}

            <div className="answer-section">
              {currentQuestion.type === 'daily_schedule' && (
                <div className="daily-schedule">
                  {SCHEDULE_SLOTS.map(slot => {
                    const value = (responses[`schedule_${slot.key}`] as string) || '';
                    return (
                      <div key={slot.key} className="schedule-row">
                        <span className="schedule-label">{slot.label}</span>
                        <select
                          className={`schedule-select${value ? ' filled' : ''}`}
                          value={value}
                          onChange={e => handleScheduleChange(slot.key, e.target.value)}
                        >
                          <option value="">—</option>
                          {slot.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}

              {currentQuestion.type === 'zipcode' && (
                <div className="zipcode-input-wrapper">
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    value={(responses[currentQuestion.id] as string) || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                      handleTextChange(val);
                    }}
                    placeholder="e.g. 10001"
                    className="zipcode-input"
                  />
                  {(() => {
                    const val = (responses[currentQuestion.id] as string) || '';
                    if (val.length > 0 && val.length < 5) {
                      return <p className="input-warning">Please enter a 5-digit zip code.</p>;
                    }
                    return null;
                  })()}
                </div>
              )}

              {currentQuestion.type === 'text' && (
                <>
                  <textarea
                    value={(responses[currentQuestion.id] as string) || ''}
                    onChange={(e) => handleTextChange(e.target.value)}
                    placeholder="Type your answer here..."
                    rows={4}
                  />
                  <p className="input-disclaimer">
                    Please do not include sensitive personal information such as health conditions, medical history, Social Security numbers, financial details, or other private data in your response.
                  </p>
                </>
              )}

              {currentQuestion.type === 'number' && (
                <input
                  type="number"
                  value={(responses[currentQuestion.id] as string) || ''}
                  onChange={(e) => handleNumberChange(e.target.value)}
                  placeholder="Enter a number"
                  className="number-input"
                />
              )}

              {currentQuestion.type === 'select' && (
                <div className="select-options">
                  {currentQuestion.options?.map((option) => (
                    <button
                      key={option}
                      className={`option-btn ${responses[currentQuestion.id] === option ? 'selected' : ''}`}
                      onClick={() => handleSelectChange(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}

              {currentQuestion.type === 'multiselect' && (
                <div className="multiselect-options">
                  {currentQuestion.options?.map((option) => (
                    <button
                      key={option}
                      className={`option-btn ${((responses[currentQuestion.id] as string[]) || []).includes(option) ? 'selected' : ''}`}
                      onClick={() => handleMultiSelectChange(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}

              {currentQuestion.type === 'sleep_schedule' && (() => {
                const data: Record<string, string> = (() => {
                  const val = responses[currentQuestion.id];
                  return val ? JSON.parse(val as string) : {};
                })();
                return (
                  <div className="sleep-schedule">
                    <div className="schedule-row">
                      <span className="schedule-label">Bedtime</span>
                      <select
                        className={`schedule-select${data.bedtime ? ' filled' : ''}`}
                        value={data.bedtime ?? ''}
                        onChange={e => handleSleepScheduleChange('bedtime', e.target.value)}
                      >
                        <option value="">—</option>
                        {BEDTIME_OPTIONS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="schedule-row">
                      <span className="schedule-label">Wake time</span>
                      <select
                        className={`schedule-select${data.wake_time ? ' filled' : ''}`}
                        value={data.wake_time ?? ''}
                        onChange={e => handleSleepScheduleChange('wake_time', e.target.value)}
                      >
                        <option value="">—</option>
                        {WAKETIME_OPTIONS.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })()}

              {currentQuestion.type === 'cooking_days' && (() => {
                const COOK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
                const DAY_FULL: Record<string, string> = {
                  Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday',
                  Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday',
                };
                const { flexible, days } = parseCookingDays(responses[currentQuestion.id]);
                const selectedDays = new Set(days.map(d => d.day));
                return (
                  <div className="cooking-days">
                    <button
                      type="button"
                      className={`cooking-flexible-btn${flexible ? ' cooking-flexible-btn--active' : ''}`}
                      onClick={() => handleCookingDaysFlexible(currentQuestion.id, flexible)}
                    >
                      I'm flexible
                    </button>
                    {!flexible && (
                      <>
                        <div className="cooking-day-pills">
                          {COOK_DAYS.map(d => (
                            <button
                              key={d}
                              type="button"
                              className={`session-pill${selectedDays.has(d) ? ' session-pill--active' : ''}`}
                              onClick={() => handleCookingDaysToggleDay(currentQuestion.id, d)}
                            >
                              {d}
                            </button>
                          ))}
                        </div>
                        {days.length > 0 && (
                          <div className="cooking-day-times">
                            {days.map(({ day, time }) => (
                              <div key={day} className="cooking-day-time-row">
                                <span className="cooking-day-label">{DAY_FULL[day]}</span>
                                <select
                                  className="session-select"
                                  value={time}
                                  onChange={e => handleCookingDaysTime(currentQuestion.id, day, e.target.value)}
                                >
                                  <option value="">Any time</option>
                                  {TIME_OPTIONS.filter(t => t.value).map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

            </div>
          </div>
        )}

        {submitError && (
          <div className="survey-submit-error" role="alert">
            <span>{submitError}</span>
            <button
              type="button"
              className="survey-submit-error-dismiss"
              aria-label="Dismiss error"
              onClick={() => setSubmitError(null)}
            >
              ×
            </button>
          </div>
        )}

        <div className="survey-navigation">
          <button
            onClick={showWorkoutBuilder ? () => setShowWorkoutBuilder(false) : handlePrevious}
            disabled={!showWorkoutBuilder && currentStep === 0}
            className="btn btn-secondary"
          >
            Previous
          </button>

          {showWorkoutBuilder ? (
            <button
              onClick={() => {
                if (builderMode === 'build') {
                  handleSubmit(workoutDays.length > 0 ? JSON.stringify(workoutDays) : undefined);
                } else {
                  handleSubmit(undefined, JSON.stringify(surveyAnswers));
                }
              }}
              disabled={
                isSubmitting ||
                (builderMode === 'build' && workoutDays.length === 0) ||
                (builderMode === 'survey' && !surveyBuilderValid)
              }
              className="btn btn-primary"
            >
              {isSubmitting ? 'Submitting...' : builderMode === 'survey' ? 'Generate My Plan' : 'Build My Routine'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="btn btn-primary"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Survey;
